import type { ApiErrorEnvelope, TokenPairResponse } from '@brainkey/shared';

import { API_BASE_URL, API_PREFIX } from '../config';

import { tokenStore } from './tokenStore';

/** A typed error carrying the API's error envelope + HTTP status. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the caller is not (or no longer) authenticated. */
  get isAuthError(): boolean {
    return this.status === 401;
  }
}

/** Called when the session is irrecoverable (refresh failed) so the app can
 * drop to the login screen. Registered by the auth layer. */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON-serialisable body, or a `FormData` instance (e.g. a file upload)
   * — sent as-is with no `Content-Type` set, so the browser attaches the
   * correct multipart boundary itself. */
  body?: unknown;
  /** Query params; `undefined`/`null` values are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Skip the Authorization header + 401 refresh (used by auth endpoints). */
  skipAuth?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${API_PREFIX}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = 'HTTP_ERROR';
  let message = res.statusText || `Request failed (${res.status})`;
  let details: unknown;
  try {
    const data = (await res.json()) as Partial<ApiErrorEnvelope>;
    if (data.error) {
      code = data.error.code ?? code;
      message = data.error.message ?? message;
      details = data.error.details;
    }
  } catch {
    /* non-JSON error body — keep defaults */
  }
  return new ApiError(res.status, code, message, details);
}

// ── Single-flight refresh ───────────────────────────────────────────
// Concurrent 401s must trigger at most one refresh; all callers await it.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return false;

  const res = await fetch(buildUrl('/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    tokenStore.clear();
    return false;
  }
  const pair = (await res.json()) as TokenPairResponse;
  tokenStore.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
  return true;
}

function ensureRefresh(): Promise<boolean> {
  refreshInFlight ??= refreshTokens().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function execute(url: string, init: RequestInit, skipAuth: boolean): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!skipAuth) {
    const token = tokenStore.getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(url, { ...init, headers });
}

/**
 * Typed JSON request against the platform API. Attaches the bearer token,
 * transparently refreshes once on a 401, and normalises errors to `ApiError`.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, skipAuth = false } = options;

  const init: RequestInit = { method };
  if (signal) init.signal = signal;
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { 'Content-Type': 'application/json' };
  }

  const url = buildUrl(path, query);
  let res = await execute(url, init, skipAuth);

  // Transparent refresh-and-retry on expiry.
  if (res.status === 401 && !skipAuth && tokenStore.getRefreshToken()) {
    const refreshed = await ensureRefresh();
    if (refreshed) {
      res = await execute(url, init, skipAuth);
    } else {
      onSessionExpired?.();
      throw new ApiError(401, 'SESSION_EXPIRED', 'Your session has expired. Please sign in again.');
    }
  }

  if (!res.ok) {
    const err = await parseError(res);
    if (err.status === 401 && !skipAuth) onSessionExpired?.();
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
